import { type Dispatch, type FC, type SetStateAction, useState } from "react"
import styled from "styled-components"

import Tooltip from "@forento/ui/components/Tooltip"
import { type UseFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import { CheckmarkIcon, CloseIcon, SettingsIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { InputTextArea } from "~/components/InputField"
import ManageTagsModal from "~/components/ManageTagsModal"

import { Description, Title } from "./styles"

type Props = {
	title: string
	shortDescription: string
	isFreeNavigation: boolean
	chapterSkippingEnabled: boolean
	commentsEnabled: boolean
	autoApproveComments: boolean
	selectedTags: number[]
	thumbnail: UseFile
	setTitle: Dispatch<SetStateAction<string>>
	setShortDescription: Dispatch<SetStateAction<string>>
	setFreeNavigation: Dispatch<SetStateAction<boolean>>
	setChapterSkippingEnabled: Dispatch<SetStateAction<boolean>>
	setCommentsEnabled: Dispatch<SetStateAction<boolean>>
	setAutoApproveComments: Dispatch<SetStateAction<boolean>>
	setSelectedTags: Dispatch<SetStateAction<number[]>>
	tags: { id: number; label: string }[]
	reloadTags(): void
}
const CourseInformation: FC<Props> = ({
	title,
	shortDescription,
	isFreeNavigation,
	chapterSkippingEnabled,
	commentsEnabled,
	autoApproveComments,
	selectedTags,
	thumbnail,
	setTitle,
	setShortDescription,
	setFreeNavigation,
	setChapterSkippingEnabled,
	setCommentsEnabled,
	setAutoApproveComments,
	setSelectedTags,
	tags,
	reloadTags,
}) => {
	const [isManageTagsModalOpen, setManageTagsModalOpen] = useState(false)

	return (
		<>
			<ManageTagsModal
				type="course"
				isOpen={isManageTagsModalOpen}
				tags={tags}
				onClose={() => setManageTagsModalOpen(false)}
				onChange={reloadTags}
			/>
			<Title>Thumbnail</Title>
			<Description>
				A picture or social icon, used as thumbnail when you share your course on social medias.
			</Description>
			<ImageUploader recommendedSize="thumbnail" {...thumbnail.imageUploader} />
			<Title>Basic information</Title>
			<Description>Information on how to discover your course online.</Description>
			<Inputs>
				<InputField label="Title" value={title} onChange={setTitle} />
				<InputTextArea label="Description" value={shortDescription} onChange={setShortDescription} />
				<div>
					<Checkbox
						label="Enable free navigation"
						isChecked={isFreeNavigation}
						onChange={setFreeNavigation}
					/>
					<CheckboxDescription>
						When enabled, students can freely navigate through the course without completing previous pages.
					</CheckboxDescription>
				</div>
				<div>
					<Checkbox
						label="Allow skipping chapters"
						isChecked={chapterSkippingEnabled}
						onChange={setChapterSkippingEnabled}
					/>
					<CheckboxDescription>
						When enabled, students can use the "Skip chapter" button to mark the rest of a chapter as
						completed. Turn this off together with free navigation to require students to complete every
						chapter in order.
					</CheckboxDescription>
				</div>
				<Checkbox label="Enable comments" isChecked={commentsEnabled} onChange={setCommentsEnabled} />
				<Checkbox
					label="Automatically approve comments"
					isChecked={autoApproveComments}
					onChange={setAutoApproveComments}
					isDisabled={!commentsEnabled}
				/>
			</Inputs>
			<Header>
				<Title>Tags</Title>
				<Tooltip tooltip="Manage tags">
					<TagSettingsButton onClick={() => setManageTagsModalOpen(true)}>
						<SettingsIcon />
					</TagSettingsButton>
				</Tooltip>
			</Header>
			<Description>Tags help students find your course when they search for specific topics.</Description>
			<div className="flex flex-wrap gap-2">
				{tags.map(tag => {
					const isSelected = selectedTags.includes(tag.id)
					const Icon = isSelected ? CheckmarkIcon : CloseIcon
					return (
						<button
							key={tag.id}
							type="button"
							className={cn(
								"flex cursor-pointer items-center gap-2 rounded-4xl px-4 py-2 text-white",
								isSelected ? "bg-primary" : "bg-light",
							)}
							onClick={() => toggleStateArray(setSelectedTags, tag.id)}
						>
							<Icon className="size-6" />
							{tag.label}
						</button>
					)
				})}
			</div>
		</>
	)
}

const Inputs = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 24px;
	margin-bottom: 10px;

	${Title} {
		margin: 0;
	}
`

const CheckboxDescription = styled.p`
	color: black;
	font-size: 14px;
	margin: 4px 0 0 28px;
`

const TagSettingsButton = styled(Button)`
	width: 24px;
	height: 24px;
`

export default CourseInformation
